import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { CoilSymbol } from '../primitives/CoilSymbol';
import { DT } from '../tokens';

/** Hydronic Q = 500·GPM·ΔT — coil with EWT/LWT thermometers, GPM flow meter. */
export function DiagramHydronicHeatTransfer() {
  const cy = 110;

  return (
    <DiagramCanvas>
      {/* Inlet pipe with insulation hatch (warm tint) */}
      <Line x1={20} y1={cy} x2={100} y2={cy} stroke={DT.stroke} strokeWidth="9" strokeLinecap="square" />
      <Line x1={20} y1={cy} x2={100} y2={cy} stroke="rgba(255,107,26,0.6)" strokeWidth="5" strokeLinecap="square" />
      <Polygon points={`92,${cy} 84,${cy - 4} 84,${cy + 4}`} fill={DT.arrow} />

      {/* In-line flow meter (paddle/turbine) */}
      <Rect x={48} y={cy - 12} width={20} height={24} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <Circle cx={58} cy={cy} r={5} fill="none" stroke={DT.varColor} strokeWidth="1" />
      <Line x1={55} y1={cy - 3} x2={61} y2={cy + 3} stroke={DT.varColor} strokeWidth="1" />
      <Line x1={55} y1={cy + 3} x2={61} y2={cy - 3} stroke={DT.varColor} strokeWidth="1" />
      <SvgText x={58} y={cy - 18} fill={DT.varColor} fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">GPM</SvgText>

      {/* Coil (heat exchanger) */}
      <CoilSymbol x={100} y={cy - 30} width={150} height={60} loops={4} />
      {/* Heat indicator wavy lines above coil */}
      {[0, 1, 2].map(i => (
        <Path key={i} d={`M${130 + i * 30},${cy - 36} q5,-6 10,0 t10,0`} fill="none" stroke="#FF6B1A" strokeWidth="1" opacity={0.6} />
      ))}

      {/* Outlet pipe (cool tint) */}
      <Line x1={250} y1={cy} x2={330} y2={cy} stroke={DT.stroke} strokeWidth="9" strokeLinecap="square" />
      <Line x1={250} y1={cy} x2={330} y2={cy} stroke="rgba(122,170,187,0.55)" strokeWidth="5" strokeLinecap="square" />
      <Polygon points={`325,${cy} 317,${cy - 4} 317,${cy + 4}`} fill={DT.arrow} />

      {/* EWT thermometer (warm) */}
      <Line x1={30} y1={cy - 5} x2={30} y2={50} stroke={DT.leader} strokeWidth="1" />
      <Circle cx={30} cy={40} r={10} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <Line x1={30} y1={40} x2={36} y2={34} stroke="#FF6B1A" strokeWidth="1.5" />
      <SvgText x={30} y={28} fill="#FF6B1A" fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">T_EWT</SvgText>

      {/* LWT thermometer (cool) */}
      <Line x1={300} y1={cy - 5} x2={300} y2={50} stroke={DT.leader} strokeWidth="1" />
      <Circle cx={300} cy={40} r={10} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <Line x1={300} y1={40} x2={295} y2={36} stroke="#7AAABB" strokeWidth="1.5" />
      <SvgText x={300} y={28} fill="#7AAABB" fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">T_LWT</SvgText>

      {/* ΔT brace */}
      <Line x1={42} y1={66} x2={288} y2={66} stroke={DT.varColor} strokeWidth="1" />
      <Polygon points={`42,66 48,69 48,63`} fill={DT.varColor} />
      <Polygon points={`288,66 282,69 282,63`} fill={DT.varColor} />
      <SvgText x={165} y={62} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔT = T_EWT − T_LWT</SvgText>

      {/* Labels */}
      <LeaderLine x1={58} y1={cy + 12} x2={20} y2={210} varName="GPM" desc="water flow" anchor="start" />
      <LeaderLine x1={175} y1={cy + 30} x2={170} y2={210} varName="Q" desc="heat (BTU/h)" anchor="middle" />
      <LeaderLine x1={300} y1={cy + 8} x2={310} y2={210} varName="ΔT" desc="water-side" anchor="end" />

      <FormulaTag formula="Q = 500 · GPM · ΔT   (water; for glycol use 485–490)" />
    </DiagramCanvas>
  );
}
